import type { Unsubscribe } from './notifier.js';

// The sync status surface, separate from state: an app draws the spinner, the offline badge and
// the "update the app" prompt from here and the data from `subscribe`.
export interface SyncStatus {
  readonly online: boolean;
  readonly syncing: boolean;
  readonly pending: number;
  // The fold-boundary latch: an incoming batch was refused as unfoldable and the aggregate is
  // read-only until the app updates.
  readonly updateRequired: boolean;
  readonly lastSyncedAt: number | undefined;
  readonly lastError: string | undefined;
}

function statusEq(a: SyncStatus, b: SyncStatus): boolean {
  return a.online === b.online && a.syncing === b.syncing && a.pending === b.pending
    && a.updateRequired === b.updateRequired && a.lastSyncedAt === b.lastSyncedAt
    && a.lastError === b.lastError;
}

// Field-diffed pub/sub: every compute() is a fresh object, so reference equality would fire on
// every emit.
export class StatusTracker {
  private readonly listeners = new Set<(s: SyncStatus) => void>();
  private last: SyncStatus | undefined;

  constructor(private readonly compute: () => SyncStatus) {}

  get value(): SyncStatus { return this.compute(); }

  subscribe(cb: (s: SyncStatus) => void): Unsubscribe {
    this.listeners.add(cb);
    return () => { this.listeners.delete(cb); };
  }

  emit(): void {
    const next = this.compute();
    if (this.last !== undefined && statusEq(this.last, next)) return;
    this.last = next;
    for (const l of this.listeners) {
      try { l(next); } catch (e) { console.error('StatusTracker: listener threw:', e); }
    }
  }
}
